/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

export interface Particle {
  x: number;
  y: number;
  tx: number;
  ty: number;
  size: number;
  vx: number;
  vy: number;
  alpha: number;
}

/**
 * Initializes the particle swarm for the breathing-pulse animation, distributing
 * particles around the canvas center with randomized velocity and target positions.
 *
 * @param W - Canvas CSS width.
 * @param H - Canvas CSS height.
 * @returns The generated particle array.
 */
export function createParticles(W: number, H: number): Particle[] {
  const cx = W / 2;
  const cy = H / 2;
  const COUNT = 240;
  return Array.from({ length: COUNT }, (_, i) => {
    const angle = (i / COUNT) * Math.PI * 2;
    const dist = 100 + Math.random() * 180;
    const tx = cx + Math.cos(angle) * dist * 0.55;
    const ty = cy + Math.sin(angle) * dist * 0.38 - 16;
    return {
      x: Math.random() * W,
      y: Math.random() * H,
      tx,
      ty,
      size: 1 + Math.random() * 2.8,
      vx: (Math.random() - 0.5) * 1.6,
      vy: (Math.random() - 0.5) * 0.9,
      alpha: 0.25 + Math.random() * 0.65,
    };
  });
}

/**
 * Computes the cubic ease-out factor that pulls particles toward their targets.
 *
 * @param elapsed - Seconds since the animation started.
 * @returns Easing factor in the range [0, 1]; 1 once fully settled.
 */
function computeSettleEase(elapsed: number): number {
  const settleStart = 1.1;
  const settleEnd = 2.7;
  const settleT = Math.max(0, Math.min((elapsed - settleStart) / (settleEnd - settleStart), 1));
  return 1 - Math.pow(1 - settleT, 3);
}

/**
 * Advances a particle toward its target and wraps it around canvas edges.
 *
 * @param p - Particle to mutate in place.
 * @param ease - Current settle easing factor.
 * @param W - Canvas width used for horizontal wrapping.
 * @param H - Canvas height used for vertical wrapping.
 */
function updateParticlePosition(p: Particle, ease: number, W: number, H: number): void {
  p.x += p.vx + (p.tx - p.x) * ease * 0.06;
  p.y += p.vy + (p.ty - p.y) * ease * 0.06;
  p.vx *= 0.97;
  p.vy *= 0.97;
  if (p.x < 0) p.x = W;
  if (p.x > W) p.x = 0;
  if (p.y < 0) p.y = H;
  if (p.y > H) p.y = 0;
}

/**
 * Draws a single particle as a filled dark-red circle at the given opacity.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param p - Particle to draw.
 * @param alpha - Final opacity applied to this particle.
 */
function renderParticle(ctx: CanvasRenderingContext2D, p: Particle, alpha: number): void {
  ctx.fillStyle = `rgba(74,14,17,${alpha})`;
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Renders one animation frame: clears the canvas, settles particles, and fades
 * them out as the dissolve phase progresses.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param particles - Particle swarm to update and draw.
 * @param elapsed - Seconds since the animation started.
 * @param W - Canvas width.
 * @param H - Canvas height.
 */
export function drawFrame(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  elapsed: number,
  W: number,
  H: number,
): void {
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#faf7f4';
  ctx.fillRect(0, 0, W, H);

  const ease = computeSettleEase(elapsed);
  const isSettling = ease < 1;
  const dissolveT = Math.max(0, Math.min((elapsed - 2.8) / 0.9, 1));

  particles.forEach((p) => {
    if (isSettling) updateParticlePosition(p, ease, W, H);
    renderParticle(ctx, p, p.alpha * (1 - dissolveT));
  });
}
